import React, { useState, useEffect } from 'react'
import { createPendingRegistrationContainer } from '../../container/pending-registration-module'
import { RegistrationMethodSelector } from './RegistrationMethodSelector'
import { EmailRegistrationForm } from './EmailRegistrationForm'
import { getUserContainer } from '../../container/user-module'
import { LOGGED_USER_SERVICE_KEY } from '../../modules/users'
import { ILoggedUserService } from '../../modules/users/services/LoggedUserService'
import { navigate } from '@reach/router'
import { useSnackbar } from 'notistack'

type Props = {
  token: string
  onSuccess?: () => void
}

export type RegistrationMethod = 'email' | 'google' | 'apple' | null

export function Registration({ token, onSuccess }: Props) {
  const [userData, setUserData] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)
  const [registrationMethod, setRegistrationMethod] = useState<RegistrationMethod>(null)
  const [isRegistrationComplete, setIsRegistrationComplete] = useState(false)
  const container = createPendingRegistrationContainer()
  const { enqueueSnackbar } = useSnackbar()

  const loggedUserService = getUserContainer().get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)

  useEffect(() => {
    const loggedUser = loggedUserService.get()
    if (loggedUser) {
      navigate('/newsWall').then()
      return
    }

    const fetchRegistrationData = async () => {
      try {
        const data = await container.pendingRegistrationService
          .getPendingRegistration(token)
          .toPromise()
        if (data) {
          setUserData(data || {})
        } else {
          setError('No se encontraron datos de registro')
        }
      } catch (e: any) {
        setError(
          'Error al cargar datos del registro: ' + (e.message || 'Token inválido o expirado')
        )
      }
    }

    if (token && registrationMethod !== 'apple' && registrationMethod !== 'google') {
      fetchRegistrationData()
    }
  }, [token, registrationMethod])

  useEffect(() => {
    const handleOAuthMessage = (event: MessageEvent) => {
      if (event.data.type === 'GOOGLE_OAUTH_ERROR' || event.data.type === 'APPLE_OAUTH_ERROR') {
        const errorMessage = event.data.error || 'Error en la autenticación'

        enqueueSnackbar(errorMessage, { variant: 'error' })

        setRegistrationMethod(null)
      }
    }

    window.addEventListener('message', handleOAuthMessage)
    return () => window.removeEventListener('message', handleOAuthMessage)
  }, [enqueueSnackbar])

  useEffect(() => {
    if (
      (registrationMethod === 'google' || registrationMethod === 'apple') &&
      isRegistrationComplete
    ) {
      enqueueSnackbar('Usuario registrado con éxito', {
        variant: 'success',
      })

      setTimeout(() => {
        navigate('/login').then(() => {
          window.location.reload()
        })
      }, 2000)
    }
  }, [registrationMethod, isRegistrationComplete, enqueueSnackbar])

  const handleMethodSelection = (method: RegistrationMethod, data?: any) => {
    setRegistrationMethod(method)

    if (method === 'google' || method === 'apple') {
      if (data && data.registrationComplete) {
        setIsRegistrationComplete(true)
        if (data.message) enqueueSnackbar(data.message, { variant: 'success' })
      }
    }
  }

  if (!registrationMethod) {
    if (error === 'No se encontraron datos de registro') {
      return (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div
            style={{
              backgroundColor: '#ffebee',
              border: '1px solid #ffcdd2',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1rem',
              color: '#c62828',
              fontFamily: 'Montserrat, sans-serif',
              maxWidth: '375px',
              margin: '0 auto',
            }}
          >
            {error}
          </div>
        </div>
      )
    }
    return (
      <RegistrationMethodSelector
        userData={userData}
        error={error}
        token={token}
        onSelectMethod={handleMethodSelection}
      />
    )
  }

  if (registrationMethod === 'google' || registrationMethod === 'apple') {
    const providerName = registrationMethod === 'google' ? 'Google' : 'Apple'

    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <h4>Procesando registro con {providerName}...</h4>
        <p>Completando tu registro, por favor espera...</p>
        <div style={{ margin: '20px 0' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: `4px solid ${registrationMethod === 'google' ? '#4285f4' : '#000000'}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto',
            }}
          ></div>
        </div>
        <button
          onClick={() => setRegistrationMethod(null)}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            backgroundColor: '#f5f5f7',
            border: '1px solid #ddd',
            borderRadius: '5px',
            cursor: 'pointer',
          }}
        >
          Volver atrás
        </button>

        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    )
  }

  return (
    <EmailRegistrationForm
      token={token}
      userData={userData}
      onSuccess={onSuccess}
      onBack={() => setRegistrationMethod(null)}
    />
  )
}
